import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { profileApi } from '../../services/api'
import { showSuccess, showError } from './appSlice'

const initialState = {
  profile: {
    name: '',
    title: '',
    bio: '',
    avatar: '',
    email: '',
    phone: '',
    location: '',
    website: '',
    socialLinks: [],
  },
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchProfile = createAsyncThunk(
  'profile/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileApi.get()
      
      if (response.success) {
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки профиля')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const updateProfile = createAsyncThunk(
  'profile/updateProfile',
  async (profileData, { dispatch, rejectWithValue }) => {
    try {
      const response = await profileApi.update(profileData)
      
      if (response.success) {
        dispatch(showSuccess('Профиль обновлен успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления профиля')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при обновлении профиля'))
      return rejectWithValue(error.message)
    }
  }
)

export const uploadAvatar = createAsyncThunk(
  'profile/uploadAvatar',
  async (file, { dispatch, rejectWithValue }) => {
    try {
      const response = await profileApi.uploadAvatar(file)
      
      if (response.success) {
        dispatch(showSuccess('Аватар обновлен успешно'))
        return response.data.avatar
      } else {
        throw new Error(response.error?.message || 'Ошибка загрузки аватара')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при загрузке аватара'))
      return rejectWithValue(error.message)
    }
  }
)

export const updateSocialLinks = createAsyncThunk(
  'profile/updateSocialLinks',
  async (socialLinks, { dispatch, rejectWithValue }) => {
    try {
      const response = await profileApi.updateSocialLinks(socialLinks)
      
      if (response.success) {
        dispatch(showSuccess('Социальные ссылки обновлены'))
        return response.data.socialLinks
      } else {
        throw new Error(response.error?.message || 'Ошибка обновления ссылок')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при обновлении ссылок'))
      return rejectWithValue(error.message)
    }
  }
)

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    updateProfileField: (state, action) => {
      const { field, value } = action.payload
      state.profile[field] = value
    },
    
    addSocialLink: (state, action) => {
      state.profile.socialLinks.push(action.payload)
    },
    
    removeSocialLink: (state, action) => {
      state.profile.socialLinks = state.profile.socialLinks.filter(
        (_, index) => index !== action.payload
      )
    },
    
    updateSocialLink: (state, action) => {
      const { index, data } = action.payload
      state.profile.socialLinks[index] = { ...state.profile.socialLinks[index], ...data }
    },
    
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // Fetch Profile
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.isLoading = false
        state.profile = { ...state.profile, ...action.payload }
        state.error = null
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
    
    // Update Profile
    builder
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false
        state.profile = { ...state.profile, ...action.payload }
        state.error = null
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
    
    // Upload Avatar
    builder
      .addCase(uploadAvatar.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(uploadAvatar.fulfilled, (state, action) => {
        state.isLoading = false
        state.profile.avatar = action.payload
        state.error = null
      })
      .addCase(uploadAvatar.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
    
    // Update Social Links
    builder
      .addCase(updateSocialLinks.fulfilled, (state, action) => {
        state.profile.socialLinks = action.payload
      })
  },
})

export const {
  updateProfileField,
  addSocialLink,
  removeSocialLink,
  updateSocialLink,
  clearError,
} = profileSlice.actions

// Selectors
export const selectPublicProfile = (state) => {
  const { profile } = state.profile
  return {
    name: profile.name,
    title: profile.title,
    bio: profile.bio,
    avatar: profile.avatar,
    location: profile.location,
    website: profile.website,
    socialLinks: profile.socialLinks,
  }
}

export default profileSlice.reducer